'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { notFound } from 'next/navigation';
import { PortalHeader } from '@/components/portal/PortalHeader';
import { PortalFooter } from '@/components/portal/PortalFooter';
import { PortalHomeView } from '@/components/portal/PortalHomeView';
import { CategoryServicesView } from '@/components/portal/CategoryServicesView';
import { ServiceDetailView } from '@/components/portal/ServiceDetailView';
import { ScheduleView } from '@/components/portal/ScheduleView';
import { ClientWhatsAppView } from '@/components/portal/ClientWhatsAppView';
import { ClientDetailsView } from '@/components/portal/ClientDetailsView';
import { ConfirmationView } from '@/components/portal/ConfirmationView';
import { SuccessView } from '@/components/portal/SuccessView';
import { MyBookingsModal } from '@/components/portal/MyBookingsModal';
import { SearchModal } from '@/components/portal/SearchModal';
import { BookingSlideOverDrawer } from '@/components/portal/BookingSlideOverDrawer';
import { AppointmentDetailsModal } from '@/components/portal/AppointmentDetailsModal';
import { ClientProfilePortalView } from '@/components/portal/ClientProfilePortalView';
import { isReservedSlug } from '@/lib/reservedSlugs';
import { CalenddyLogo } from '@/components/brand/CalenddyLogo';
import { DirectBranchCatalogView } from '@/components/portal/DirectBranchCatalogView';
import { getThemeTokens } from '@/lib/themeTokens';
import { Service, Category, Staff, TimeSlot, DateItem, PortalView } from '@/components/portal/types';
import { Store, ArrowLeft, Calendar, Sparkles, User } from 'lucide-react';

interface BookingClientProps {
  slug: string;
  branchSlug?: string;
  initialStaffSlug?: string;
  hasMultipleBranches?: boolean;
  initialBusiness?: any;
  initialBranch?: any;
  initialServices?: Service[];
  initialCategories?: Category[];
  initialStaffList?: Staff[];
}

function slugify(text: string): string {
  if (!text) return '';
  return text
    .toString()
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9 -]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

export function BookingClient({
  slug,
  branchSlug,
  initialStaffSlug,
  hasMultipleBranches,
  initialBusiness,
  initialBranch,
  initialServices,
  initialCategories,
  initialStaffList
}: BookingClientProps) {
  // Si el slug pertenece a una ruta reservada del sistema, rechazar
  if (isReservedSlug(slug)) {
    notFound();
  }

  // Navegación de Vistas del Portal Público
  const [view, setView] = useState<PortalView>('home');
  const [viewHistory, setViewHistory] = useState<PortalView[]>(['home']);

  // Modales
  const [isMyBookingsOpen, setIsMyBookingsOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // Slide-over Drawer de Reserva y Detalles de Cita (Fiel a Calendico Cerini)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [activeDrawerService, setActiveDrawerService] = useState<Service | null>(null);
  const [reschedulingBooking, setReschedulingBooking] = useState<any | null>(null);
  const [selectedDetailsBooking, setSelectedDetailsBooking] = useState<any | null>(null);
  const [profileInitialBooking, setProfileInitialBooking] = useState<any | null>(null);

  // Datos del Negocio y Sucursal inicializados con SSR
  const [business, setBusiness] = useState<any>(initialBusiness || null);
  const [currentBranch, setCurrentBranch] = useState<any>(initialBranch || null);
  const [services, setServices] = useState<Service[]>(initialServices || []);
  const [categories, setCategories] = useState<Category[]>(initialCategories || []);
  const [staffList, setStaffList] = useState<Staff[]>(initialStaffList || []);
  const [loadingInitial, setLoadingInitial] = useState<boolean>(!initialBusiness);
  const [notFoundBusiness, setNotFoundBusiness] = useState(false);

  // Selección de Servicios y Carrito
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [selectedServices, setSelectedServices] = useState<Service[]>([]);
  const [selectedStaffId, setSelectedStaffId] = useState<string>(() => {
    if (initialStaffSlug && initialStaffList && initialStaffList.length > 0) {
      const matched = initialStaffList.find(
        (st) => slugify(st.name) === initialStaffSlug || st.id === initialStaffSlug
      );
      if (matched) return matched.id;
    }
    return 'any';
  });

  // Fechas y Disponibilidad
  const [availableDates, setAvailableDates] = useState<DateItem[]>(() => {
    if (!initialBusiness) return [];
    const maxDays = initialBusiness.maxAdvanceDays || 30;
    const daysMap = ['DOM', 'LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB'];
    const monthsMap = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    const newDates: DateItem[] = [];
    const today = new Date();
    for (let i = 0; i < maxDays; i++) {
      const d = new Date();
      d.setDate(today.getDate() + i);
      const dayOfWeek = d.getDay();
      const isWorkingDay = dayOfWeek !== 0;
      if (isWorkingDay) {
        newDates.push({
          dayName: daysMap[dayOfWeek],
          dayNum: d.getDate().toString(),
          monthShort: monthsMap[d.getMonth()],
          fullDate: d.toISOString().split('T')[0],
          isWorkingDay
        });
      }
    }
    return newDates;
  });
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [slots, setSlots] = useState<TimeSlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedTime, setSelectedTime] = useState<string>('');

  // Identificación del Cliente
  const [clientPhone, setClientPhone] = useState<string>('');
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [isForAnotherPerson, setIsForAnotherPerson] = useState(false);
  const [anotherPersonName, setAnotherPersonName] = useState<string>('');

  // Estado de Envío
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [confirmedBooking, setConfirmedBooking] = useState<any>(null);

  const navigateTo = (newView: PortalView) => {
    setViewHistory((prev) => [...prev, newView]);
    setView(newView);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    if (viewHistory.length > 1) {
      const nextHistory = [...viewHistory];
      nextHistory.pop();
      const prevView = nextHistory[nextHistory.length - 1];
      setViewHistory(nextHistory);
      setView(prevView);
    } else {
      setView('home');
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Cargar datos del negocio según el slug específico
  useEffect(() => {
    let isMounted = true;

    async function loadBusinessData() {
      setLoadingInitial(true);
      setNotFoundBusiness(false);

      try {
        const branchQuery = branchSlug ? `&branchSlug=${encodeURIComponent(branchSlug)}` : '';
        const [resBiz, resSrv, resStaff] = await Promise.all([
          fetch(`/api/business?slug=${encodeURIComponent(slug)}`),
          fetch(`/api/services?slug=${encodeURIComponent(slug)}${branchQuery}`),
          fetch(`/api/staff?slug=${encodeURIComponent(slug)}${branchQuery}`)
        ]);

        if (!resBiz.ok) {
          if (isMounted) setNotFoundBusiness(true);
          return;
        }

        const dataBiz = await resBiz.json();
        const dataSrv = await resSrv.json();
        const dataStaff = await resStaff.json();

        if (!dataBiz.success || !dataBiz.business) {
          if (isMounted) setNotFoundBusiness(true);
          return;
        }

        if (isMounted) {
          setBusiness(dataBiz.business);

          // Determinar sucursal activa
          if (dataSrv.branch) {
            setCurrentBranch(dataSrv.branch);
          } else if (dataBiz.business.branches && dataBiz.business.branches.length > 0) {
            const match = branchSlug
              ? dataBiz.business.branches.find((b: any) => b.slug === branchSlug)
              : dataBiz.business.branches[0];
            setCurrentBranch(match || dataBiz.business.branches[0]);
          }

          if (dataSrv.success && dataSrv.services) {
            setServices(dataSrv.services);
            if (dataSrv.categories) {
              setCategories(dataSrv.categories);
            }
          }
          if (dataStaff.success && dataStaff.staff) {
            setStaffList(dataStaff.staff);
            if (initialStaffSlug) {
              const matched = dataStaff.staff.find(
                (st: Staff) => slugify(st.name) === initialStaffSlug || st.id === initialStaffSlug
              );
              if (matched) {
                setSelectedStaffId(matched.id);
              }
            }
          }

          // Generar fechas considerando maxAdvanceDays del negocio
          const maxDays = dataBiz.business.maxAdvanceDays || 30;
          const daysMap = ['DOM', 'LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB'];
          const monthsMap = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
          const newDates: DateItem[] = [];
          const today = new Date();

          for (let i = 0; i < maxDays; i++) {
            const d = new Date();
            d.setDate(today.getDate() + i);
            const dayOfWeek = d.getDay();
            const isWorkingDay = dayOfWeek !== 0; // domingo cerrado por defecto
            if (isWorkingDay) {
              const dayName = daysMap[dayOfWeek];
              const dayNum = d.getDate().toString();
              const monthShort = monthsMap[d.getMonth()];
              const fullDate = d.toISOString().split('T')[0];
              newDates.push({ dayName, dayNum, monthShort, fullDate, isWorkingDay });
            }
          }

          if (newDates.length > 0) {
            setAvailableDates(newDates);
            setSelectedDate(newDates[0].fullDate);
          }
        }
      } catch (err) {
        console.warn('Error loading business booking portal:', err);
        if (isMounted) setNotFoundBusiness(true);
      } finally {
        if (isMounted) setLoadingInitial(false);
      }
    }

    loadBusinessData();
    return () => {
      isMounted = false;
    };
  }, [slug, branchSlug]);

  // Cargar disponibilidad de turnos en vivo desde PostgreSQL
  useEffect(() => {
    if (!selectedDate || selectedServices.length === 0 || notFoundBusiness) return;

    async function loadAvailability() {
      setLoadingSlots(true);
      try {
        const totalDuration = selectedServices.reduce((acc, s) => acc + s.duration, 0);
        const serviceIds = selectedServices.map((s) => s.id).join(',');
        const targetBranchSlug = branchSlug || currentBranch?.slug || '';
        const branchParam = targetBranchSlug ? `&branchSlug=${encodeURIComponent(targetBranchSlug)}` : '';
        const res = await fetch(
          `/api/availability?slug=${encodeURIComponent(slug)}&date=${selectedDate}&serviceIds=${serviceIds}&duration=${totalDuration}&staffId=${selectedStaffId}${branchParam}`
        );
        const data = await res.json();
        if (data.success) {
          setSlots(data.slots || []);
        } else {
          setSlots([]);
        }
      } catch (e) {
        console.error('Error cargando slots:', e);
        setSlots([]);
      } finally {
        setLoadingSlots(false);
      }
    }

    loadAvailability();
  }, [slug, branchSlug, currentBranch?.slug, selectedDate, selectedStaffId, selectedServices, notFoundBusiness]);

  const handleSelectCategory = (catId: string) => {
    const cat = categories.find((c) => c.id === catId);
    if (cat) {
      setSelectedCategory(cat);
      navigateTo('category_services');
    }
  };

  const handleSelectService = (service: Service) => {
    setActiveDrawerService(service);
    setReschedulingBooking(null);
    setIsDrawerOpen(true);
  };

  const handleRescheduleFromModal = (booking: any) => {
    const match =
      services.find((s) => s.id === booking.serviceId || s.name === booking.serviceName) ||
      services[0] || {
        id: booking.serviceId || 'srv_corte',
        name: booking.serviceName || 'Corte de hombre',
        description: '',
        duration: booking.duration || 30,
        cashPrice: booking.price || 49000,
        listPrice: booking.price || 49000,
        depositRequired: false,
        depositAmount: 0,
        category: 'Corte',
        staff: []
      };
    setActiveDrawerService(match);
    setReschedulingBooking(booking);
    setIsDrawerOpen(true);
  };

  const handleAddServiceToCart = (service: Service) => {
    if (!selectedServices.some((s) => s.id === service.id)) {
      setSelectedServices((prev) => [...prev, service]);
    }
  };

  const handleRemoveServiceFromCart = (serviceId: string) => {
    if (selectedServices.length > 1) {
      setSelectedServices((prev) => prev.filter((s) => s.id !== serviceId));
    }
  };

  // Staff y fecha formateada
  const currentStaffObj = staffList.find((s) => s.id === selectedStaffId);
  const assignedStaffName = currentStaffObj
    ? currentStaffObj.name
    : selectedServices[0]?.staff?.[0]?.name || 'Profesional asignado';

  const currentDateObj = availableDates.find((d) => d.fullDate === selectedDate);
  const formattedDateString = currentDateObj
    ? `${currentDateObj.dayName} ${currentDateObj.dayNum} De ${currentDateObj.monthShort}`
    : selectedDate;

  // Confirmar reserva atómica en PostgreSQL
  const handleConfirmBooking = async () => {
    if (!selectedServices.length || !selectedTime || !selectedDate || !firstName.trim() || !lastName.trim()) {
      setErrorMessage('Faltan datos obligatorios para completar la reserva.');
      return;
    }

    setSubmitting(true);
    setErrorMessage('');

    try {
      const primaryService = selectedServices[0];
      const additionalServices = selectedServices.slice(1);
      const totalCashPrice = selectedServices.reduce((acc, s) => acc + s.cashPrice, 0);
      const fullName = `${firstName.trim()} ${lastName.trim()}`;

      const res = await fetch('/api/bookings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessSlug: slug,
          branchSlug: branchSlug || currentBranch?.slug,
          branchId: currentBranch?.id,
          serviceId: primaryService.id,
          serviceIds: selectedServices.map((s) => s.id),
          staffId: selectedStaffId,
          date: selectedDate,
          time: selectedTime,
          clientName: fullName,
          clientPhone: clientPhone.trim(),
          clientEmail: email.trim() || undefined,
          subProfileName: isForAnotherPerson ? anotherPersonName.trim() : undefined,
          notes:
            additionalServices.length > 0
              ? `[Multi-servicio: ${selectedServices.map((s) => s.name).join(' + ')}]`
              : '',
          isOverturn: false,
        }),
      });

      const data = await res.json();

      if (data.success && data.appointment) {
        setConfirmedBooking(data.appointment);

        // Guardar reserva en localStorage con scope estricto por negocio
        try {
          const storageKey = `calenddy_bookings_${slug}`;
          const stored = localStorage.getItem(storageKey);
          const existing = stored ? JSON.parse(stored) : [];
          existing.unshift({
            id: data.appointment.id,
            businessSlug: slug,
            businessName: business?.name || slug,
            securityCode: data.appointment.securityCode,
            accessToken: data.appointment.accessToken,
            serviceName: data.appointment.serviceName || primaryService.name,
            staffName: assignedStaffName,
            dateStr: selectedDate,
            time: selectedTime,
            price: data.appointment.price || totalCashPrice,
            depositAmount: data.appointment.depositAmount || 0,
            status: data.appointment.status || 'confirmed',
            items: data.appointment.items || [],
            createdAt: new Date().toISOString(),
          });
          localStorage.setItem(storageKey, JSON.stringify(existing));

          // Guardar perfil del cliente para este negocio
          const profileKey = `calenddy_client_profile_${slug}`;
          localStorage.setItem(
            profileKey,
            JSON.stringify({
              phone: clientPhone,
              firstName: firstName.trim(),
              lastName: lastName.trim(),
              name: fullName,
              email: email.trim()
            })
          );
        } catch (e) {
          console.error('Error guardando reserva localmente:', e);
        }

        navigateTo('success');
      } else {
        if (data.code === 'SLOT_NOT_AVAILABLE') {
          setErrorMessage('El horario seleccionado acaba de ser reservado. Por favor elegí otro horario disponible.');
          setView('schedule');
        } else {
          setErrorMessage(data.error || 'No se pudo confirmar la reserva.');
        }
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Error de comunicación con el servidor.');
    } finally {
      setSubmitting(false);
    }
  };

  // Pantalla 404 si el negocio no existe
  if (notFoundBusiness && !loadingInitial) {
    return (
      <div className="min-h-screen bg-[#F7F7FA] flex flex-col font-sans">
        <header className="w-full bg-white border-b border-slate-100 py-4 px-6 flex items-center justify-between">
          <CalenddyLogo href="/" size="sm" />
          <Link
            href="/ingresar"
            className="text-xs font-bold text-[#6255F5] hover:underline"
          >
            Acceso para Negocios →
          </Link>
        </header>

        <div className="flex-1 flex items-center justify-center p-6">
          <div className="max-w-md w-full bg-white rounded-3xl p-8 border border-slate-200/80 shadow-sm text-center space-y-5">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center">
              <Store className="w-7 h-7" />
            </div>

            <div className="space-y-2">
              <h1 className="text-xl font-bold text-[#191923]">Negocio no encontrado</h1>
              <p className="text-xs text-slate-500 leading-relaxed">
                La dirección <code className="px-2 py-0.5 bg-slate-100 rounded text-slate-800 font-semibold font-mono">calenddy.com/{slug}</code> no corresponde a ningún comercio registrado o no se encuentra activo actualmente.
              </p>
            </div>

            <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/"
                className="w-full sm:w-auto px-5 py-2.5 bg-[#6255F5] hover:bg-[#5144E5] text-white text-xs font-bold rounded-xl transition shadow-sm inline-flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Ir a Calenddy</span>
              </Link>

              <Link
                href="/registro"
                className="w-full sm:w-auto px-5 py-2.5 bg-[#ECEAFF] hover:bg-[#dedaff] text-[#6255F5] text-xs font-bold rounded-xl transition inline-flex items-center justify-center gap-1.5"
              >
                <Sparkles className="w-4 h-4" />
                <span>Crear mi agenda</span>
              </Link>
            </div>
          </div>
        </div>

        <footer className="py-6 text-center text-xs text-slate-400">
          Potenciado por <strong className="text-slate-600">Calenddy</strong> · Sistema de Reservas Online
        </footer>
      </div>
    );
  }

  const brandColor = business?.brandColor || '#6255F5';
  const theme = getThemeTokens(business?.portalTheme, business?.brandColor);
  const displayName = currentBranch?.name
    ? `${business?.name || slug} · ${currentBranch.name}`
    : business?.name || slug;

  // Transición directa y limpia: Sin pantalla de carga intermedia ni marca Calenddy cargando
  if (!business && loadingInitial) {
    return (
      <div
        style={{ backgroundColor: theme.bgPage }}
        className="min-h-screen flex flex-col font-sans transition-colors"
      />
    );
  }

  // VISTA 1: HOME (Catálogo directo inspirado en Cerini)
  if (view === 'home') {
    return (
      <div
        style={{ backgroundColor: theme.bgPage, color: theme.textPrimary }}
        className="min-h-screen flex flex-col font-sans selection:bg-slate-300/30 transition-colors"
      >
        <DirectBranchCatalogView
          business={business}
          currentBranch={currentBranch}
          allBranches={business?.branches || []}
          hasMultipleBranches={hasMultipleBranches || (business?.branches && business.branches.length > 1)}
          categories={categories}
          services={services}
          staffList={staffList}
          selectedStaffId={selectedStaffId}
          onSelectStaff={setSelectedStaffId}
          onSelectService={handleSelectService}
          onOpenMyBookings={() => {
            setProfileInitialBooking(null);
            navigateTo('profile');
          }}
        />

        {/* Slide-over Drawer (Apertura lateral derecha fiel a Calendico Cerini) */}
        <BookingSlideOverDrawer
          isOpen={isDrawerOpen}
          onClose={() => {
            setIsDrawerOpen(false);
            setReschedulingBooking(null);
          }}
          service={activeDrawerService}
          business={business}
          currentBranch={currentBranch}
          staffList={staffList}
          preSelectedStaffId={selectedStaffId}
          initialBooking={reschedulingBooking}
          onOpenAppointmentDetails={(booking) => {
            setIsDrawerOpen(false);
            setProfileInitialBooking(booking);
            navigateTo('profile');
          }}
          onRescheduleSuccess={(updatedBooking) => {
            setIsDrawerOpen(false);
            setProfileInitialBooking(updatedBooking);
            navigateTo('profile');
          }}
        />

        {/* Modal: Mis Turnos ("Tus citas" idéntico a Cerini) */}
        <MyBookingsModal
          isOpen={isMyBookingsOpen}
          onClose={() => setIsMyBookingsOpen(false)}
          brandColor={brandColor}
          businessName={business?.name || slug}
          businessSlug={slug}
          onRescheduleFromModal={handleRescheduleFromModal}
        />

        {/* Modal: Detalles de Cita (Inspección con 4 iconos y SweetAlert2) */}
        {selectedDetailsBooking && (
          <AppointmentDetailsModal
            isOpen={Boolean(selectedDetailsBooking)}
            onClose={() => setSelectedDetailsBooking(null)}
            booking={selectedDetailsBooking}
            businessName={business?.name || slug}
            businessSlug={slug}
            branchName={currentBranch?.name || 'Sede Principal'}
            branchAddress={currentBranch?.address}
            branchPhone={currentBranch?.phone}
            onReschedule={(booking) => {
              setSelectedDetailsBooking(null);
              handleRescheduleFromModal(booking);
            }}
          />
        )}

        {/* Modal: Buscador */}
        <SearchModal
          isOpen={isSearchOpen}
          onClose={() => setIsSearchOpen(false)}
          services={services}
          onSelectService={handleSelectService}
        />
      </div>
    );
  }

  // VISTA DEDICADA: MI PERFIL ("Tus citas" en la Splash Page del comercio como en Cerini)
  if (view === 'profile') {
    return (
      <ClientProfilePortalView
        business={business}
        currentBranch={currentBranch}
        onBackToCatalog={() => {
          setProfileInitialBooking(null);
          navigateTo('home');
        }}
        onRescheduleFromProfile={(booking) => {
          setProfileInitialBooking(null);
          handleRescheduleFromModal(booking);
          navigateTo('home');
        }}
        initialSelectedBooking={profileInitialBooking}
      />
    );
  }

  // VISTAS DEL FLUJO DE RESERVA (Schedule, WhatsApp, Datos, Confirmación, Éxito)
  return (
    <div
      style={{ backgroundColor: theme.bgPage, color: theme.textPrimary }}
      className="min-h-screen flex flex-col font-sans selection:bg-slate-300/30 transition-colors"
    >
      {/* Barra superior de navegación para pasos de reserva */}
      <header
        style={{
          backgroundColor: theme.bgSurface,
          borderColor: theme.borderSubtle
        }}
        className="sticky top-0 z-40 border-b px-4 sm:px-8 py-3 transition-colors"
      >
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-3">
          <button
            onClick={handleBack}
            style={{ color: theme.textPrimary }}
            className="flex items-center gap-1.5 text-xs font-semibold hover:opacity-80 transition cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Volver al catálogo</span>
            <span className="sm:hidden">Volver</span>
          </button>

          <span
            style={{ color: theme.textPrimary }}
            className="text-xs font-bold truncate max-w-[200px] sm:max-w-xs"
          >
            {displayName}
          </span>

          <button
            onClick={() => setIsMyBookingsOpen(true)}
            style={{
              backgroundColor: theme.isDark ? '#1F1F24' : '#18181B',
              color: '#FFFFFF',
              borderColor: theme.isDark ? '#2E2E36' : '#27272A'
            }}
            className="text-xs font-semibold px-3 py-1.5 rounded-xl border flex items-center gap-1.5 hover:opacity-90 transition cursor-pointer shadow-xs"
          >
            <User className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Mis reservas</span>
          </button>
        </div>
      </header>

      {/* Cuerpo Principal Dinámico según Vista */}
      <main className="flex-1 max-w-5xl w-full mx-auto px-4 pt-6 pb-12">
        {/* VISTA 2: LISTA DE SERVICIOS POR CATEGORÍA */}
        {view === 'category_services' && selectedCategory && (
          <CategoryServicesView
            category={selectedCategory}
            services={services}
            onBack={handleBack}
            onSelectService={handleSelectService}
          />
        )}

        {/* VISTA 3: DETALLE DE SERVICIO Y CARRITO */}
        {view === 'service_detail' && selectedServices[0] && (
          <ServiceDetailView
            primaryService={selectedServices[0]}
            selectedServices={selectedServices}
            staffList={staffList}
            selectedStaffId={selectedStaffId}
            onSelectStaff={setSelectedStaffId}
            onRemoveService={handleRemoveServiceFromCart}
            onAddAnotherService={() => {
              if (selectedCategory) {
                navigateTo('category_services');
              } else {
                navigateTo('home');
              }
            }}
            onProceedToSchedule={() => navigateTo('schedule')}
            onBack={handleBack}
          />
        )}

        {/* VISTA 4: CALENDARIO DE DÍAS Y SLOTS */}
        {view === 'schedule' && selectedServices[0] && (
          <ScheduleView
            businessName={displayName}
            portalTheme={business?.portalTheme}
            brandColor={brandColor}
            primaryService={selectedServices[0]}
            selectedServices={selectedServices}
            allServices={services}
            assignedStaffName={assignedStaffName}
            availableDates={availableDates}
            selectedDate={selectedDate}
            onSelectDate={setSelectedDate}
            slots={slots}
            loadingSlots={loadingSlots}
            selectedTime={selectedTime}
            onSelectTime={setSelectedTime}
            onAddServiceToCart={handleAddServiceToCart}
            onRemoveServiceFromCart={handleRemoveServiceFromCart}
            onProceedToClientData={() => navigateTo('client_whatsapp')}
            onBack={handleBack}
          />
        )}

        {/* VISTA 5: IDENTIFICACIÓN POR WHATSAPP */}
        {view === 'client_whatsapp' && selectedServices[0] && (
          <ClientWhatsAppView
            businessName={displayName}
            portalTheme={business?.portalTheme}
            brandColor={brandColor}
            primaryService={selectedServices[0]}
            selectedServices={selectedServices}
            assignedStaffName={assignedStaffName}
            formattedDateString={formattedDateString}
            selectedTime={selectedTime}
            phone={clientPhone}
            onChangePhone={setClientPhone}
            onProceed={() => navigateTo('client_details')}
            onBack={handleBack}
          />
        )}

        {/* VISTA 6: COMPLETAR NOMBRE Y APELLIDO */}
        {view === 'client_details' && selectedServices[0] && (
          <ClientDetailsView
            businessName={displayName}
            portalTheme={business?.portalTheme}
            brandColor={brandColor}
            primaryService={selectedServices[0]}
            selectedServices={selectedServices}
            assignedStaffName={assignedStaffName}
            formattedDateString={formattedDateString}
            selectedTime={selectedTime}
            phone={clientPhone}
            firstName={firstName}
            lastName={lastName}
            email={email}
            onChangeFirstName={setFirstName}
            onChangeLastName={setLastName}
            onChangeEmail={setEmail}
            onEditPhone={() => navigateTo('client_whatsapp')}
            onProceed={() => navigateTo('confirmation')}
            onBack={handleBack}
          />
        )}

        {/* VISTA 7: CONFIRMACIÓN Y TITULARIDAD */}
        {view === 'confirmation' && selectedServices[0] && (
          <ConfirmationView
            businessName={displayName}
            portalTheme={business?.portalTheme}
            brandColor={brandColor}
            primaryService={selectedServices[0]}
            selectedServices={selectedServices}
            assignedStaffName={assignedStaffName}
            formattedDateString={formattedDateString}
            selectedTime={selectedTime}
            fullName={`${firstName.trim()} ${lastName.trim()}`}
            phone={clientPhone}
            isForAnotherPerson={isForAnotherPerson}
            anotherPersonName={anotherPersonName}
            onChangeIsForAnotherPerson={setIsForAnotherPerson}
            onChangeAnotherPersonName={setAnotherPersonName}
            onChangeClientData={() => navigateTo('client_details')}
            onConfirmBooking={handleConfirmBooking}
            onBack={handleBack}
            submitting={submitting}
            errorMessage={errorMessage}
          />
        )}

        {/* VISTA 8: CONFIRMADO / ÉXITO */}
        {view === 'success' && (
          <SuccessView
            businessName={displayName}
            portalTheme={business?.portalTheme}
            brandColor={brandColor}
            confirmedBooking={confirmedBooking}
            selectedServices={selectedServices}
            assignedStaffName={assignedStaffName}
            formattedDateString={formattedDateString}
            selectedTime={selectedTime}
            onGoHome={() => {
              setSelectedServices([]);
              setSelectedTime('');
              setView('home');
              setViewHistory(['home']);
            }}
            onOpenMyBookings={() => setIsMyBookingsOpen(true)}
          />
        )}
      </main>

      {/* Footer Discreto */}
      <footer
        style={{
          borderColor: theme.borderSubtle,
          backgroundColor: theme.bgSurface,
          color: theme.textMuted
        }}
        className="w-full border-t py-6 px-4 text-center text-xs mt-auto transition-colors"
      >
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <p>© {new Date().getFullYear()} {displayName}</p>
          <div className="flex items-center gap-1.5">
            <span>Powered by</span>
            <CalenddyLogo size="xs" variant={theme.isDark ? 'light' : 'monochrome'} />
          </div>
        </div>
      </footer>

      {/* Modal: Mis Turnos */}
      <MyBookingsModal
        isOpen={isMyBookingsOpen}
        onClose={() => setIsMyBookingsOpen(false)}
        brandColor={brandColor}
      />

      {/* Modal: Buscador */}
      <SearchModal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        services={services}
        onSelectService={handleSelectService}
      />
    </div>
  );
}
